/* ============================================
   Catálogo del partner — lo que vende

   Dos cosas en una lista, y la diferencia importa:

   - **Los suyos**: servicios que él hace, con el precio al que los vende.
   - **Los nuestros**: lo que nos subcontrata. El precio que aparece es lo
     que NOSOTROS le cobramos a él — su costo, no su precio de venta.

   Por eso la columna no se llama «precio» a secas: sobre un servicio nuestro
   dice «tu costo», y confundir los dos es cotizarle al cliente lo que uno
   paga. Qué es cada cual lo decide el servidor (`es_propio`), no esta
   pantalla.

   No hay un precio de reventa guardado a propósito: al mismo servicio él le
   pone 5.000 a un cliente y 1.400 a otro, así que el precio de venta vive en
   el ítem de cada cotización.
   ============================================ */
import { useMemo, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { useCachedQuery } from '@/hooks/useCachedQuery'
import { useQueryClient } from '@tanstack/react-query'
import { formatCurrency } from '@/lib/utils'
import { showToast } from '@/components/layout/Toast'
import type { Servicio } from '@/lib/types'
import { Badge, PageHeader, Pagination } from '@/components/ui'
import { usePagination } from '@/hooks/usePagination'
import EmptyState from '@/components/shared/EmptyState'
import { IconPackage, IconPlus, IconSearch, IconTrash, IconEdit } from '@/components/icons/Icons'

const CRUMBS = [{ label: 'CRM' }, { label: 'Servicios' }]
const TODOS = 'todos'

interface Borrador {
  id?: number
  nombre: string
  descripcion: string
  precio_base: string
  moneda: string
}

const VACIO: Borrador = { nombre: '', descripcion: '', precio_base: '', moneda: 'PEN' }

export default function PartnerServicios() {
  const api = useApi()
  const queryClient = useQueryClient()
  const { data: servicios = [], isLoading } = useCachedQuery<Servicio>('servicios')

  const [search, setSearch] = useState('')
  const [filtroDueno, setFiltroDueno] = useState(TODOS)
  const [editando, setEditando] = useState<Borrador | null>(null)
  const [guardando, setGuardando] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const filtrados = useMemo(() => {
    const q = search.trim().toLowerCase()
    return servicios.filter(s => {
      if (filtroDueno === 'propios' && !s.es_propio) return false
      if (filtroDueno === 'nuestros' && s.es_propio) return false
      if (!q) return true
      return [s.nombre, s.descripcion].some(c => c?.toLowerCase().includes(q))
    })
  }, [servicios, search, filtroDueno])

  const firma = `${search}|${filtroDueno}`
  const { page, setPage, totalPages, paginated, pageSize, total } = usePagination(filtrados, 15, firma)

  const propios = servicios.filter(s => s.es_propio).length

  async function guardar() {
    if (!editando) return
    if (!editando.nombre.trim()) { setError('Falta el nombre'); return }
    const precio = parseFloat(editando.precio_base || '0')
    if (isNaN(precio) || precio < 0) { setError('El precio no es válido'); return }

    setGuardando(true)
    setError(null)
    try {
      const cuerpo = {
        nombre: editando.nombre.trim(),
        descripcion: editando.descripcion.trim() || null,
        precio_base: precio,
        moneda: editando.moneda,
      }
      if (editando.id) await api.put(`/api/servicios/${editando.id}`, cuerpo)
      else await api.post('/api/servicios', cuerpo)

      queryClient.invalidateQueries({ queryKey: ['servicios'] })
      setEditando(null)
      showToast(editando.id ? 'Servicio actualizado' : 'Servicio agregado', 'success')
    } catch (err: any) {
      setError(err?.message || 'No se pudo guardar')
    } finally { setGuardando(false) }
  }

  async function borrar(s: Servicio) {
    if (!window.confirm(`¿Quitar «${s.nombre}» de tu catálogo?`)) return
    try {
      await api.del(`/api/servicios/${s.id}`)
      queryClient.invalidateQueries({ queryKey: ['servicios'] })
      showToast('Servicio eliminado', 'success')
    } catch (err: any) {
      showToast(err?.message || 'No se pudo eliminar', 'error')
    }
  }

  if (isLoading) return (
    <div className="page-enter">
      <PageHeader breadcrumbs={CRUMBS} />
      <div className="empty-state"><p>Cargando servicios...</p></div>
    </div>
  )

  const hayFiltros = search.trim() !== '' || filtroDueno !== TODOS

  return (
    <div className="page-enter page-shell">
      <PageHeader
        breadcrumbs={CRUMBS}
        meta={total === servicios.length
          ? `${servicios.length} ${servicios.length === 1 ? 'servicio' : 'servicios'}`
          : `${total} de ${servicios.length}`}
        actions={
          <button type="button" className="btn btn-sm btn-primary" onClick={() => { setEditando({ ...VACIO }); setError(null) }}>
            <IconPlus size={14} /> Agregar servicio
          </button>
        }
      />

      <div className="filters-bar">
        <div className="filters-bar__search">
          <IconSearch size={14} className="filters-bar__search-icon" />
          <input
            className="form-input"
            placeholder="Buscar por nombre o descripción..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <select className="form-input" value={filtroDueno} onChange={e => setFiltroDueno(e.target.value)} style={{ width: 200 }}>
          <option value={TODOS}>Todos</option>
          <option value="propios">Los míos</option>
          <option value="nuestros">De 1Conecta</option>
        </select>
        {hayFiltros && (
          <button className="btn btn-sm btn-secondary filters-bar__clear" onClick={() => { setSearch(''); setFiltroDueno(TODOS) }}>
            Limpiar filtros
          </button>
        )}
      </div>

      {editando && (
        <div className="ld-modal-fondo" role="dialog" aria-modal="true" aria-label="Servicio">
          <div className="ld-modal ld-modal--ancho">
            <h3 className="ld-modal__titulo">{editando.id ? 'Editar servicio' : 'Agregar un servicio'}</h3>
            <p className="ld-modal__texto">
              Es tuyo y solo lo ves tú. Al cotizar sale como punto de partida; el precio
              final lo pones en cada cotización, porque no a todos los clientes les cobras
              lo mismo.
            </p>

            <div className="pp-grid-campos">
              <div className="form-group pp-ancho">
                <label className="form-label">Nombre</label>
                <input
                  className="form-input"
                  value={editando.nombre}
                  onChange={e => setEditando({ ...editando, nombre: e.target.value })}
                  placeholder="Diseño de marca, Campaña de performance..."
                  autoFocus
                />
              </div>
              <div className="form-group pp-ancho">
                <label className="form-label">Descripción <span className="form-hint">opcional</span></label>
                <textarea
                  className="form-input"
                  rows={3}
                  value={editando.descripcion}
                  onChange={e => setEditando({ ...editando, descripcion: e.target.value })}
                  placeholder="Qué incluye, en una o dos líneas"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Precio de referencia</label>
                <div className="subir-cot__monto">
                  <select
                    className="form-input"
                    value={editando.moneda}
                    onChange={e => setEditando({ ...editando, moneda: e.target.value })}
                  >
                    <option value="PEN">S/</option>
                    <option value="USD">$</option>
                    <option value="EUR">€</option>
                  </select>
                  <input
                    className="form-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={editando.precio_base}
                    onChange={e => setEditando({ ...editando, precio_base: e.target.value })}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>

            {error && <p className="pp-aviso pp-aviso--error">{error}</p>}

            <div className="ld-modal__acciones">
              <button type="button" className="btn btn-secondary" onClick={() => setEditando(null)} disabled={guardando}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={guardar} disabled={guardando}>
                {guardando ? 'Guardando...' : editando.id ? 'Guardar' : 'Agregar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {servicios.length === 0 ? (
        <EmptyState
          icon={<IconPackage size={48} />}
          title="Sin servicios"
          description="Acá va lo que vendes: tus propios servicios con tus precios, y los de 1Conecta que nos subcontratas, con lo que te cobramos. Al cotizar los agregas con un clic."
          actionLabel="Agregar servicio"
          onAction={() => { setEditando({ ...VACIO }); setError(null) }}
        />
      ) : filtrados.length === 0 ? (
        <div className="empty-state">
          <h3>Ningún servicio coincide</h3>
          <p>Prueba con otros filtros.</p>
          <button className="btn btn-secondary" onClick={() => { setSearch(''); setFiltroDueno(TODOS) }}>Limpiar filtros</button>
        </div>
      ) : (
        <>
          <div className="table-container page-shell__table">
            <table>
              <thead>
                <tr>
                  <th>Servicio</th>
                  <th>De quién</th>
                  <th style={{ textAlign: 'right' }}>Precio / costo</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {paginated.map(s => (
                  <tr key={s.id}>
                    <td className="serv-celda">
                      <div className="cell-stack">
                        <span className="cell-stack__title">{s.nombre}</span>
                        {s.descripcion && <span className="cell-stack__sub serv-desc">{s.descripcion}</span>}
                      </div>
                    </td>
                    <td>
                      {s.es_propio
                        ? <Badge variant="aceptada">Tuyo</Badge>
                        : <Badge variant="neutral">1Conecta</Badge>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="cell-stack">
                        <span className="cell-stack__title">{formatCurrency(Number(s.precio_base || 0), s.moneda)}</span>
                        {/* El precio de un servicio nuestro es lo que le cobramos
                            a él. Decirle «precio» a secas lo invita a cotizarle
                            eso mismo a su cliente y no ganar nada. */}
                        <span className="cell-stack__sub">{s.es_propio ? 'tu precio' : 'tu costo'}</span>
                      </div>
                    </td>
                    <td>
                      {s.es_propio && (
                        <div className="row-actions row-actions--nowrap">
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => {
                              setEditando({
                                id: s.id,
                                nombre: s.nombre,
                                descripcion: s.descripcion || '',
                                precio_base: String(s.precio_base ?? ''),
                                moneda: s.moneda || 'PEN',
                              })
                              setError(null)
                            }}
                            title="Editar"
                          >
                            <IconEdit size={13} />
                          </button>
                          <button type="button" className="btn btn-sm btn-danger" onClick={() => borrar(s)} title="Quitar del catálogo">
                            <IconTrash size={13} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} noun="servicios" />
        </>
      )}

      {propios === 0 && servicios.length > 0 && (
        <p className="pp-nivel-ayuda">
          Todavía no cargaste ninguno tuyo. Los de 1Conecta son los que nos subcontratas:
          el importe que ves es lo que te cobramos a ti.
        </p>
      )}
    </div>
  )
}
